/**
 * Header component for the SiteSubmissions page
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Inbox, RefreshCw, Zap } from 'lucide-react';
import type { SubmissionStats } from '../../types/submissions';

interface SubmissionsHeaderProps {
  siteName: string;
  siteId: string;
  stats: SubmissionStats | null;
  isLoading: boolean;
  onRefresh: () => void;
}

/** Get the next tier upgrade info */
function getUpgradeInfo(planName: string): { nextTier: string; nextCap: number } | null {
  switch (planName) {
    case 'FREE':
      return { nextTier: 'Starter', nextCap: 500 };
    case 'STARTER':
      return { nextTier: 'Professional', nextCap: 5000 };
    default:
      return null;
  }
}

export const SubmissionsHeader: React.FC<SubmissionsHeaderProps> = ({
  siteName,
  siteId,
  stats,
  isLoading,
  onRefresh,
}) => {
  const usagePercent = stats ? Math.min(100, (stats.total / stats.cap) * 100) : 0;
  const isNearLimit = usagePercent >= 80;
  const upgradeInfo = stats ? getUpgradeInfo(stats.planName) : null;

  return (
    <header className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 sm:py-6 gap-4">
          {/* Left side: Back link and title */}
          <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
            <Link
              to={`/sites/${siteId}`}
              className="flex items-center space-x-1 sm:space-x-2 text-gray-600 hover:text-gray-900 transition-colors shrink-0"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Back to Site</span>
            </Link>
            <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
            <div className="min-w-0 flex items-center space-x-3">
              <Inbox className="w-6 h-6 text-primary-600 shrink-0" />
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                  Form Submissions
                </h1>
                <p className="text-sm text-gray-600 hidden sm:block truncate">{siteName}</p>
              </div>
            </div>
          </div>

          {/* Right side: Stats and refresh */}
          <div className="flex items-center space-x-4">
            {stats && (
              <div className="flex items-center space-x-4 text-sm">
                {/* Usage indicator with progress bar */}
                <div className="flex flex-col items-end">
                  <div className="text-gray-600 mb-1">
                    <span className="font-semibold text-gray-900">
                      {stats.total.toLocaleString()}
                    </span>
                    <span className="text-gray-400"> / </span>
                    <span className="text-gray-600">{stats.cap.toLocaleString()}</span>
                    <span className="text-gray-500 ml-1 hidden sm:inline">submissions</span>
                  </div>
                  {/* Progress bar */}
                  <div className="w-24 sm:w-32 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        isNearLimit ? 'bg-amber-500' : 'bg-primary-500'
                      }`}
                      style={{ width: `${usagePercent}%` }}
                    />
                  </div>
                </div>
                {stats.unread > 0 && (
                  <div className="text-primary-600 hidden sm:block">
                    <span className="font-semibold">{stats.unread}</span> unread
                  </div>
                )}
                {stats.todayCount > 0 && (
                  <div className="text-green-600 hidden md:block">
                    <span className="font-semibold">{stats.todayCount}</span> today
                  </div>
                )}
              </div>
            )}
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Upgrade prompt for FREE and STARTER tiers */}
        {upgradeInfo && (
          <div className="pb-4">
            <div
              className={`flex items-center justify-between px-4 py-3 rounded-lg ${
                isNearLimit
                  ? 'bg-amber-50 border border-amber-200'
                  : 'bg-gray-50 border border-gray-200'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Zap className={`w-4 h-4 ${isNearLimit ? 'text-amber-600' : 'text-gray-500'}`} />
                <span className={`text-sm ${isNearLimit ? 'text-amber-800' : 'text-gray-600'}`}>
                  {isNearLimit ? (
                    <>
                      You're approaching your limit.{' '}
                      <span className="hidden sm:inline">
                        Upgrade to {upgradeInfo.nextTier} for {upgradeInfo.nextCap.toLocaleString()}{' '}
                        submissions per site.
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="hidden sm:inline">
                        You're on the <span className="font-medium">{stats?.planName}</span>{' '}
                        plan.{' '}
                      </span>
                      Upgrade to {upgradeInfo.nextTier} for {upgradeInfo.nextCap.toLocaleString()}{' '}
                      submissions per site.
                    </>
                  )}
                </span>
              </div>
              <Link
                to="/pricing"
                className={`shrink-0 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  isNearLimit
                    ? 'bg-amber-600 text-white hover:bg-amber-700'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                Upgrade
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
